import * as THREE from 'three';
import { CAMO_BY_ID, WEAPON_BY_ID } from '@vm/content';
import type { ModelGeometry } from '../../game/render/voxelModel';
import { weaponGeometry, weaponObject } from '../../game/render/weaponMeshes';

/** Camo id for the model builder: undefined for the factory finish ('none') and unknown ids. */
export function activeCamo(camo: string | undefined): string | undefined {
  return camo && camo !== 'none' && CAMO_BY_ID[camo] ? camo : undefined;
}

export interface WeaponView {
  /** Lit + glow meshes, origin at the grip, barrel toward -Z. */
  object: THREE.Group;
  /** Model bounds in meters (same frame as `object`). */
  bounds: THREE.Box3;
}

const boundsCache = new WeakMap<ModelGeometry, THREE.Box3>();

/**
 * A weapon (optionally camo'd, via @vm/content's camo-aware buildWeaponModel) as a mesh group.
 * Geometry comes from the game's weapon cache (per weapon + camo, bounded, shared with the
 * game) and is never disposed by previews.
 */
export function weaponView(weaponId: string | undefined, camo?: string, scope?: string): WeaponView | null {
  const def = weaponId ? WEAPON_BY_ID[weaponId] : undefined;
  if (!def) return null;
  const c = activeCamo(camo);
  const w = weaponGeometry(def.num, c, scope);
  if (!w) return null;
  let bounds = boundsCache.get(w.geo);
  if (!bounds) {
    bounds = new THREE.Box3();
    for (const g of [w.geo.lit, w.geo.glow, w.detail?.lit, w.detail?.glow, w.attach?.lit, w.attach?.glow]) {
      if (!g) continue;
      g.computeBoundingBox();
      if (g.boundingBox) bounds.union(g.boundingBox);
    }
    boundsCache.set(w.geo, bounds);
  }
  return { object: weaponObject(def.num, true, c, scope), bounds };
}
